/**
 * The RDS Migration tab.
 *
 * The tab used to be ONE layout (RdsHealthPanel.js): a long scroll of
 * cards. It answered every question an operator could ask, but it made
 * them scroll past four sections to find the one they wanted, and the
 * "is this coming from Supabase or from RDS?" answer was spread across a
 * headline, a diagram, a card grid and a flag table.
 *
 * It was then rebuilt as FIVE layouts of the SAME facts. Every other
 * layout has since been removed: the tab now shows ONE layout --
 *
 *   A  Command Center  -- one big verdict, then the two systems, then a map
 *
 * -- and the layout picker is gone with them, because there is nothing
 * left to pick between. Command Center reads the same normalised view
 * model (rdsViewModel.js) and shares the same operator actions as every
 * layout it replaces.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, HStack, VStack, Text, Button, Spinner, Alert, AlertIcon, useToast,
  Badge, Tooltip, Switch,
} from "@chakra-ui/react";
import {
  setCutoverFlag, syncMirror, getSessionToken, rdsBaseUrl,
  setSourceMode, setCatchup, repairKeys, getRdsActivity, setLocalRdsSwitch,
} from "../../../utils/rdsPhi";
import { syncStorage } from "../../../utils/rdsStorage";
import { buildViewModel } from "./rdsViewModel";
import { samplePayload, SAMPLE_STATES } from "./samplePayload";
import { operatorErrorMessage, sourceModeErrorMessage } from "./sourceModeErrors";
import {
  IndependenceDialog, MirrorRollbackDialog, CatchupOffDialog, LocalPauseDialog, LocalEnableDialog,
} from "./SourceModeDialogs";
import TwoWaySyncDialog from "../TwoWaySyncDialog";
import PhiLogsDialog from "../PhiLogsDialog";
import LayoutACommandCenter from "./LayoutACommandCenter";

/** How often the live view re-reads the health payload and RDS's activity. */
export const LIVE_POLL_MS = 10000;
/** The window RDS's activity aggregate is asked for (one RDS accepts). */
export const ACTIVITY_WINDOW_SECONDS = 300;

/** The one layout this tab renders, and how it reads. */
export const LAYOUTS = [
  {
    id: "a",
    name: "Command Center",
    tag: "A",
    blurb: "One big verdict on top, the two systems underneath, then a map of every table.",
    Component: LayoutACommandCenter,
  },
];

/**
 * The sample-data banner. It is loud on purpose: sample data must never be
 * mistaken for the live state of the operator's own RDS.
 */
function SampleBanner({ state }) {
  const meta = SAMPLE_STATES.find((s) => s.id === state);
  return (
    <HStack
      spacing={3}
      bg="orange.50"
      borderWidth="1px"
      borderColor="orange.300"
      borderRadius="xl"
      px={4}
      py={2.5}
      align="flex-start"
      data-testid="rds-sample-banner"
    >
      <Badge colorScheme="orange" fontSize="10px" px={2} flexShrink={0}>SAMPLE DATA</Badge>
      <Box>
        <Text fontSize="xs" fontWeight="bold" color="orange.800">
          {meta?.name || state} — this is sample data, not your live RDS
        </Text>
        <Text fontSize="xs" color="orange.700">
          {meta?.blurb}
        </Text>
      </Box>
    </HStack>
  );
}

/**
 * The sample-data controls. Reading the dashboard needs the four states
 * that matter, and needs them without a live RDS.
 */
function SampleControls({ demo, onDemo, sampleState, onSampleState }) {
  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      px={3}
      py={2.5}
      boxShadow="0 1px 2px rgba(16,24,40,0.04)"
      data-testid="rds-sample-controls"
    >
      <HStack justify="space-between" align="center" spacing={4} flexWrap="wrap">
        <HStack spacing={3}>
          <Switch
            size="sm"
            colorScheme="orange"
            isChecked={demo}
            onChange={(e) => onDemo(e.target.checked)}
            data-testid="rds-sample-toggle"
          />
          <Box>
            <Text fontSize="xs" fontWeight="bold" color="gray.700">Sample data</Text>
            <Text fontSize="xs" color="gray.500">
              Read the dashboard without touching your live RDS. Every button below stays read-only.
            </Text>
          </Box>
        </HStack>
        {demo && (
          <HStack spacing={1} flexWrap="wrap">
            {SAMPLE_STATES.map((s) => {
              const active = sampleState === s.id;
              return (
                <Tooltip key={s.id} label={s.blurb} placement="bottom" hasArrow>
                  <Button
                    size="xs"
                    variant={active ? "solid" : "ghost"}
                    colorScheme={active ? "orange" : "gray"}
                    onClick={() => onSampleState(s.id)}
                    aria-pressed={active}
                    data-testid={`rds-sample-state-${s.id}`}
                  >
                    {s.name}
                  </Button>
                </Tooltip>
              );
            })}
          </HStack>
        )}
      </HStack>
    </Box>
  );
}

/**
 * The workspace: loads the health payload, owns the operator actions, and
 * hands both to the layout.
 */
function RdsWorkspace({ load, Layout, demo, sampleState, onEnableDemo }) {
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [logsOpen, setLogsOpen] = useState(false);
  // Realtime: RDS's own activity aggregate (what it actually served), the
  // live-refresh switch, and when the view last changed.
  const [activity, setActivity] = useState(null);
  const [activityError, setActivityError] = useState(null);
  const [liveOn, setLiveOn] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [liveError, setLiveError] = useState(null);
  const polling = useRef(false);
  const toast = useToast();

  // RDS's /admin/activity is a second, independent witness: it says what RDS
  // served from its own audit log. It is optional -- a paused or unreachable
  // RDS simply leaves it out and the panel still renders.
  const loadActivity = useCallback(async () => {
    try {
      const a = await getRdsActivity({ window: ACTIVITY_WINDOW_SECONDS, tail: 20 });
      setActivity(a || null);
      setActivityError(null);
    } catch (err) {
      setActivity(null);
      setActivityError(err?.message || String(err));
    }
  }, []);

  // `quiet` is the live poll: it never blanks the view. A failed poll keeps
  // the last good payload and says so in the live bar; a failed explicit
  // refresh (first load, after an action) still shows the error panel.
  const refresh = useCallback(({ quiet = false } = {}) => {
    if (quiet) {
      if (polling.current) return;
      polling.current = true;
    } else {
      setLoading(true);
      setError(null);
    }
    const healthDone = load()
      .then((h) => {
        setHealth(h);
        setUpdatedAt(new Date().toISOString());
        setLiveError(null);
      })
      .catch((e) => {
        if (quiet) setLiveError(e?.message || String(e));
        else setError(e?.message || String(e));
      });
    Promise.all([healthDone, loadActivity()]).finally(() => {
      if (quiet) polling.current = false;
      else setLoading(false);
    });
  }, [load, loadActivity]);

  useEffect(() => { if (!demo) refresh(); }, [refresh, demo]);

  // The live poll. Skipped while the tab is hidden (nothing is watching) and
  // while the previous poll is still in flight.
  useEffect(() => {
    if (demo || !liveOn) return undefined;
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      refresh({ quiet: true });
    }, LIVE_POLL_MS);
    return () => clearInterval(id);
  }, [demo, liveOn, refresh]);

  const fail = useCallback((title, err) => {
    toast({ title, description: operatorErrorMessage(err), status: "error", duration: 8000, isClosable: true });
  }, [toast]);

  const sourceModeFail = useCallback((title, err) => {
    toast({ title, description: sourceModeErrorMessage(err), status: "error", duration: 10000, isClosable: true });
  }, [toast]);

  // Flip one cutover flag. RDS answers with the new flag payload; it is
  // shown at once (the flag's `effective` is already re-evaluated
  // server-side), then the whole health view is re-read.
  const toggleFlag = useCallback(async (callSite, enabled) => {
    setBusy(callSite);
    try {
      const payload = await setCutoverFlag(callSite, enabled, `SuperAdmin panel ${enabled ? "enable" : "disable"}`);
      setHealth((h) => (h ? { ...h, readiness: { ...h.readiness, cutover: payload } } : h));
      const flag = payload?.flags?.[callSite];
      toast({
        title: `${callSite} ${enabled ? "enabled" : "disabled"}`,
        description: flag
          ? (flag.effective ? "Effective now — reads switch within 30 s." : `Not effective: ${flag.reason || "see panel"}`)
          : undefined,
        status: enabled && flag && !flag.effective ? "warning" : "success",
        duration: 6000,
        isClosable: true,
      });
      refresh();
    } catch (err) {
      fail(`Could not ${enabled ? "enable" : "disable"} ${callSite}`, err);
    } finally {
      setBusy(null);
    }
  }, [toast, refresh, fail]);

  // Refresh both mirrors now rather than waiting for the scheduler. While
  // RDS is independent the same route skips the whole-table mirror and
  // runs the catch-up (or only the access sync), and says so with
  // `skipped_full_mirror`.
  const runSync = useCallback(async () => {
    setBusy("sync");
    try {
      const result = await syncMirror();
      const summary = result?.summary || {};
      const roles = result?.access_sync?.viewer_roles_written != null
        ? ` · ${result.access_sync.viewer_roles_written} roles` : "";
      const errorTail = result?.error ? ` — ${result.error}` : "";
      if (result?.skipped_full_mirror) {
        const catchupRan = result.mode === "catchup";
        toast({
          title: result?.ok
            ? (catchupRan ? "Catch-up reconciled" : "Access roles synced")
            : "Reconcile finished with problems",
          description: (catchupRan
            ? `full mirror skipped (independent) · ${summary.changed ?? 0} changed, ${summary.orphans ?? 0} orphans`
            : "full mirror skipped (independent, catch-up off)")
            + roles + errorTail,
          status: result?.ok ? "success" : "warning",
          duration: 8000,
          isClosable: true,
        });
      } else {
        toast({
          title: result?.ok ? "Mirror refreshed" : "Mirror sync finished with problems",
          description: `${summary.ok ?? "?"} of ${summary.tables ?? "?"} tables ok`
            + (summary.failed ? `, ${summary.failed} failed` : "")
            + roles + errorTail,
          status: result?.ok ? "success" : "warning",
          duration: 8000,
          isClosable: true,
        });
      }
      refresh();
    } catch (err) {
      fail("Mirror sync failed", err);
    } finally {
      setBusy(null);
    }
  }, [toast, refresh, fail]);

  const goIndependent = useCallback(async ({ confirm, note, catchup, force }) => {
    setBusy("source-mode");
    try {
      const result = await setSourceMode("independent", { confirm, note, catchup, force });
      toast({
        title: "RDS is now independent",
        description: `Source of truth for PHI is RDS · catch-up ${result?.source_mode?.catchup === false ? "off" : "on"}`
          + " · overlay flags forced on.",
        status: "success",
        duration: 8000,
        isClosable: true,
      });
      setDialog(null);
    } catch (err) {
      sourceModeFail("Could not make RDS independent", err);
    } finally {
      setBusy(null);
      refresh();
    }
  }, [toast, refresh, sourceModeFail]);

  const repairTableKeys = useCallback(async (table) => {
    setBusy("repair");
    try {
      const result = await repairKeys(table);
      const s = result?.summary || {};
      const left = Array.isArray(result?.tables_without_id_pk) ? result.tables_without_id_pk : [];
      toast({
        title: result?.ok ? `${table} recreated keyed on id` : `Repair of ${table} finished with problems`,
        description: `${s.ok ?? "?"} of ${s.tables ?? "?"} tenant copies ok`
          + (left.length ? ` · still without a key on id: ${left.join(", ")}` : " · every PHI table is keyed on id")
          + (result?.error ? ` — ${result.error}` : ""),
        status: result?.ok ? "success" : "warning",
        duration: 10000,
        isClosable: true,
      });
    } catch (err) {
      sourceModeFail(`Could not repair ${table}`, err);
    } finally {
      setBusy(null);
    }
  }, [toast, sourceModeFail]);

  // RDS's OWN mode -> mirror: re-clones RDS from Supabase. Global (every
  // server follows RDS's row); reached only through the "RDS's own mode" menu.
  const goMirror = useCallback(async ({ confirm, note }) => {
    setBusy("source-mode");
    try {
      const result = await setSourceMode("mirror", { confirm, note });
      toast({
        title: "RDS is back in mirror mode",
        description: result?.warning || "The whole-table mirror resumes on the next sync.",
        status: "success",
        duration: 10000,
        isClosable: true,
      });
      setDialog(null);
    } catch (err) {
      sourceModeFail("Could not return to mirror mode", err);
    } finally {
      setBusy(null);
      refresh();
    }
  }, [toast, refresh, sourceModeFail]);

  // THIS NotesAI's own switch ("Use Supabase only" / "Enable RDS" since
  // 2026-10-24): POST /api/admin/rds-local-switch/ on our backend. RDS is
  // never contacted, so RDS's mode, jobs and every other server stay as
  // they are. The errors are the backend's, not RDS's, so they are worded
  // here rather than through sourceModeErrors.
  const localSwitchErrorMessage = (err) => {
    const status = err?.statusCode;
    if (status === 401) return "The NotesAI backend did not accept this session — sign in again.";
    if (status === 403) return "This account is not a SuperAdmin on the NotesAI backend.";
    if (status) return `The NotesAI backend returned ${status}${err?.detail ? `: ${err.detail}` : ""}.`;
    return err?.message || "The NotesAI backend could not be reached.";
  };
  const applyLocalSwitch = useCallback(async (disabled, note) => {
    setBusy("local-switch");
    try {
      const state = await setLocalRdsSwitch(disabled, { note: note || undefined });
      const where = state?.server ? ` (${state.server})` : "";
      toast({
        title: disabled ? "Using Supabase only in this NotesAI" : "RDS enabled in this NotesAI",
        description: disabled
          ? `Only this NotesAI${where} stopped using RDS. RDS itself was not changed and keeps serving every other server.`
          : `This NotesAI${where} follows RDS's own mode again. Nothing was changed on RDS.`,
        status: "success",
        duration: 10000,
        isClosable: true,
      });
      setDialog(null);
    } catch (err) {
      toast({
        title: disabled ? "Could not switch this NotesAI to Supabase only" : "Could not enable RDS in this NotesAI",
        description: localSwitchErrorMessage(err),
        status: "error",
        duration: 10000,
        isClosable: true,
      });
    } finally {
      setBusy(null);
      refresh();
    }
  }, [toast, refresh]);

  const applyCatchup = useCallback(async (enabled, note) => {
    setBusy("catchup");
    try {
      await setCatchup(enabled, { note });
      toast({ title: `Catch-up reconciliation ${enabled ? "on" : "off"}`, status: "success", duration: 6000, isClosable: true });
      setDialog(null);
    } catch (err) {
      sourceModeFail(`Could not turn catch-up ${enabled ? "on" : "off"}`, err);
    } finally {
      setBusy(null);
      refresh();
    }
  }, [toast, refresh, sourceModeFail]);

  const onCatchupChange = useCallback((enabled) => {
    if (enabled) applyCatchup(true, "SuperAdmin panel catch-up on");
    else setDialog("catchup-off");
  }, [applyCatchup]);

  const runStorageSync = useCallback(async () => {
    setBusy("sync-storage");
    try {
      const result = await syncStorage();
      const s = result?.summary || {};
      toast({
        title: result?.ok ? "Buckets mirrored" : "Bucket sync finished with problems",
        description: `${s.ok ?? "?"} of ${s.buckets ?? "?"} buckets ok · ${s.upserted ?? 0} new or changed, `
          + `${s.unchanged ?? 0} unchanged, ${s.pruned ?? 0} pruned, ${s.skipped ?? 0} skipped`
          + (result?.error ? ` — ${result.error}` : ""),
        status: result?.ok ? "success" : "warning",
        duration: 10000,
        isClosable: true,
      });
      refresh();
    } catch (err) {
      fail("Bucket sync failed", err);
    } finally {
      setBusy(null);
    }
  }, [toast, refresh, fail]);

  const openAdmin = useCallback(async () => {
    setBusy("open");
    try {
      const token = await getSessionToken();
      if (!token) {
        fail("Could not open RDS admin", new Error("No active session — sign in again."));
        return;
      }
      window.open(`${rdsBaseUrl()}/admin#token=${encodeURIComponent(token)}`, "_blank", "noopener");
    } finally {
      setBusy(null);
    }
  }, [fail]);

  const actions = useMemo(() => ({
    toggleFlag,
    syncMirror: runSync,
    syncStorage: runStorageSync,
    openAdmin,
    // This NotesAI's own switch (never touches RDS)...
    useSupabaseOnly: () => setDialog("local-pause"),
    enableRds: () => setDialog("local-enable"),
    // ...and RDS's own mode (global; the "RDS's own mode" menu).
    goIndependent: () => setDialog("independent"),
    goMirror: () => setDialog("mirror"),
    twoWaySync: () => setDialog("two-way"),
    setCatchup: onCatchupChange,
    openLogs: () => setLogsOpen(true),
    refresh: () => refresh(),
  }), [toggleFlag, runSync, runStorageSync, openAdmin, onCatchupChange, refresh]);

  const sample = samplePayload(sampleState);
  const vm = useMemo(() => {
    if (demo) {
      return buildViewModel(sample.payload, { buildOn: sample.buildOn, activity: sample.activity });
    }
    return health ? buildViewModel(health, { activity }) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [health, activity, demo, sampleState]);

  // What the live bar shows: whether the view refreshes itself, when it last
  // did, and whether the last poll or the activity read failed.
  const live = useMemo(() => ({
    on: liveOn && !demo,
    setOn: setLiveOn,
    updatedAt: demo ? null : updatedAt,
    error: demo ? null : liveError,
    activityError: demo ? null : activityError,
    pollMs: LIVE_POLL_MS,
    demo,
  }), [liveOn, demo, updatedAt, liveError, activityError]);

  if (error && !demo) {
    return (
      <VStack align="stretch" spacing={3}>
        <Alert status="error">
          <AlertIcon />
          Could not reach the health endpoint: {error}
        </Alert>
        <Text fontSize="xs" color="gray.500">
          You can still read the dashboard against sample data — nothing below touches your RDS.
        </Text>
        <Box>
          <Button size="sm" colorScheme="orange" onClick={onEnableDemo} data-testid="rds-sample-enable">
            Show sample data
          </Button>
        </Box>
      </VStack>
    );
  }
  if (!vm) {
    return (
      <Box py={10} textAlign="center">
        {loading ? <Spinner /> : <Text fontSize="sm" color="gray.500">Nothing to show yet.</Text>}
      </Box>
    );
  }

  return (
    <>
      {demo && <SampleBanner state={sampleState} />}
      <Layout vm={vm} actions={actions} busy={busy} live={live} />
      <PhiLogsDialog isOpen={logsOpen} onClose={() => setLogsOpen(false)} health={health} />
      <TwoWaySyncDialog
        isOpen={dialog === "two-way"}
        onClose={() => setDialog(null)}
        onApplied={refresh}
      />
      <IndependenceDialog
        isOpen={dialog === "independent"}
        onClose={() => setDialog(null)}
        onSync={runSync}
        onSubmit={goIndependent}
        onRepair={repairTableKeys}
        busy={busy}
        enabling={vm?.sourceMode?.mode === "supabase_only"}
      />
      <MirrorRollbackDialog
        isOpen={dialog === "mirror"}
        onClose={() => setDialog(null)}
        onSubmit={goMirror}
        busy={busy}
      />
      <LocalPauseDialog
        isOpen={dialog === "local-pause"}
        onClose={() => setDialog(null)}
        onSubmit={({ note }) => applyLocalSwitch(true, note)}
        busy={busy}
        vm={vm}
      />
      <LocalEnableDialog
        isOpen={dialog === "local-enable"}
        onClose={() => setDialog(null)}
        onSubmit={() => applyLocalSwitch(false)}
        busy={busy}
        vm={vm}
      />
      <CatchupOffDialog
        isOpen={dialog === "catchup-off"}
        onClose={() => setDialog(null)}
        onSubmit={(note) => applyCatchup(false, note)}
        busy={busy}
      />
    </>
  );
}

export default function RdsMigrationLayouts({ load }) {
  const [demo, setDemo] = useState(false);
  const [sampleState, setSampleState] = useState("partial");

  // There is one layout, so there is nothing to pick and nothing to
  // remember. The registry is kept so a new layout has one obvious place
  // to be registered.
  const Layout = LAYOUTS[0].Component;

  return (
    <VStack align="stretch" spacing={4}>
      <SampleControls
        demo={demo}
        onDemo={setDemo}
        sampleState={sampleState}
        onSampleState={setSampleState}
      />
      <RdsWorkspace
        load={load}
        Layout={Layout}
        demo={demo}
        sampleState={sampleState}
        onEnableDemo={() => setDemo(true)}
      />
    </VStack>
  );
}
